import { alpha } from "@mui/material/styles";

// SETUP COLORS - Enhanced Professional Bright Theme
const GREY = {
  0: "#FFFFFF",
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
  1000: "#020617",
};

const PRIMARY = {
  lighter: "#E0F2FE",
  light: "#7DD3FC",
  main: "#e96c0e", // keep a solid color for main
  dark: "#0284C7",
  darker: "#0369A1",
  contrastText: "#FFFFFF",
  hover: "#f8a24b", // lighter shade for hover
  gradient: "linear-gradient(135deg, #e96c0e, #f8a24b)", // primary gradient
  gradientAlt: "linear-gradient(90deg, #f66500, #ff9900, #ffcc00)", // secondary gradient
  soft: alpha("#f66500", 0.08),
};

const SECONDARY = {
  lighter: "#FCE7F3",
  light: "#FBCFE8",
  main: "#EC4899",
  dark: "#DB2777",
  darker: "#BE185D",
  contrastText: "#FFFFFF",
  hover: "#DB2777",
  gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
  soft: alpha("#EC4899", 0.08),
};

const INFO = {
  lighter: "#E0F2FE",
  light: "#7DD3FC",
  main: "#06B6D4",
  dark: "#0891B2",
  darker: "#0E7490",
  contrastText: "#FFFFFF",
  hover: "#0891B2",
  gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  soft: alpha("#06B6D4", 0.08),
};

const SUCCESS = {
  lighter: "#DCFCE7",
  light: "#86EFAC",
  main: "#10B981",
  dark: "#059669",
  darker: "#047857",
  contrastText: "#FFFFFF",
  hover: "#059669",
  gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  soft: alpha("#10B981", 0.08),
};

const WARNING = {
  lighter: "#FEF3C7",
  light: "#FDE68A",
  main: "#F59E0B",
  dark: "#D97706",
  darker: "#B45309",
  contrastText: "#FFFFFF",
  hover: "#D97706",
  gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
  soft: alpha("#F59E0B", 0.08),
};

const ERROR = {
  lighter: "#FEE2E2",
  light: "#FCA5A5",
  main: "#EF4444",
  dark: "#DC2626",
  darker: "#B91C1C",
  contrastText: "#FFFFFF",
  hover: "#DC2626",
  gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
  soft: alpha("#EF4444", 0.08),
};

// Additional accent colors
const ACCENT = {
  purple: "#8B5CF6",
  indigo: "#6366F1",
  teal: "#14B8A6",
  orange: "#F97316",
  pink: "#F472B6",
  yellow: "#EAB308",
  lime: "#84CC16",
  emerald: "#059669",
  sky: "#f66500",
  violet: "#7C3AED",
};

const palette = {
  common: { black: "#000000", white: "#FFFFFF" },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  accent: ACCENT,
  grey: GREY,
  divider: alpha(GREY[300], 0.6),
  text: {
    primary: GREY[800],
    secondary: GREY[600],
    disabled: GREY[500],
    inverse: "#FFFFFF",
    muted: GREY[500],
    link: PRIMARY.main,
    linkHover: PRIMARY.dark,
  },
  background: {
    paper: "#FFFFFF",
    default: GREY[50],
    neutral: GREY[100],
    elevated: "#FFFFFF",
    subtle: GREY[50],
    inverse: GREY[900],
    overlay: alpha(GREY[900], 0.5),
  },
  action: {
    active: GREY[600],
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    activatedOpacity: 0.12,
  },
  // Enhanced surface colors
  surface: {
    primary: alpha(PRIMARY.main, 0.04),
    secondary: alpha(SECONDARY.main, 0.04),
    success: alpha(SUCCESS.main, 0.04),
    warning: alpha(WARNING.main, 0.04),
    error: alpha(ERROR.main, 0.04),
    info: alpha(INFO.main, 0.04),
  },
  // Enhanced border colors
  border: {
    light: GREY[200],
    main: GREY[300],
    dark: GREY[400],
    primary: alpha(PRIMARY.main, 0.3),
    secondary: alpha(SECONDARY.main, 0.3),
    success: alpha(SUCCESS.main, 0.3),
    warning: alpha(WARNING.main, 0.3),
    error: alpha(ERROR.main, 0.3),
  },
};

export default palette;
